'use client';

import * as React from "react";
import { cn } from "../../utils/cn";

/**
 * Flag — simplified heraldry SVG flags for the 6 regions the platform tracks.
 *
 * Rendering approach:
 *   - Fixed 3:2 aspect ratio; the `size` prop sets width in px (height auto).
 *   - Inline SVG with a rounded clip so flags read well at chip sizes (16–24px)
 *     and hero sizes (48–96px). No external images, no font requirements.
 *   - Region codes mirror the Chip/region and region-format util set.
 *
 * These are simplified for scale legibility, not geopolitically exact — they
 * match the Figma "Flag/*" components on page "28 · Component Library · MV5".
 */

export type FlagRegion = "NZ" | "PK" | "SA" | "CA" | "US" | "Other";

export interface FlagProps extends Omit<React.SVGProps<SVGSVGElement>, "children"> {
  region: FlagRegion;
  /** Width in pixels. Height is set automatically for the 3:2 aspect ratio. */
  size?: number;
  /** Extra classes. */
  className?: string;
  /** Accessible label; defaults to the region code. Set aria-hidden if decorative. */
  title?: string;
}

const bodies: Record<FlagRegion, React.ReactNode> = {
  // New Zealand — royal blue field with a red-and-white Southern Cross
  // (simplified to 4 stars) and a small Union Jack corner canton.
  NZ: (
    <>
      <rect width="60" height="40" fill="#00247d" />
      <rect x="0" y="0" width="24" height="16" fill="#00247d" />
      <path d="M0 0 L24 16 M24 0 L0 16" stroke="#ffffff" strokeWidth="3" />
      <path d="M0 0 L24 16 M24 0 L0 16" stroke="#cc142b" strokeWidth="1.5" />
      <path d="M12 0 V16 M0 8 H24" stroke="#ffffff" strokeWidth="4" />
      <path d="M12 0 V16 M0 8 H24" stroke="#cc142b" strokeWidth="2" />
      <circle cx="45" cy="12" r="1.6" fill="#cc142b" />
      <circle cx="52" cy="17" r="2.0" fill="#cc142b" />
      <circle cx="42" cy="24" r="1.8" fill="#cc142b" />
      <circle cx="49" cy="30" r="1.6" fill="#cc142b" />
    </>
  ),
  // Pakistan — dark green field with white hoist stripe, crescent + star.
  PK: (
    <>
      <rect width="60" height="40" fill="#01411c" />
      <rect x="0" y="0" width="15" height="40" fill="#ffffff" />
      <circle cx="36" cy="20" r="9" fill="#ffffff" />
      <circle cx="39" cy="18" r="8" fill="#01411c" />
      <polygon
        points="46,16 47.2,19.2 50.5,19.2 47.8,21.2 48.9,24.4 46,22.5 43.1,24.4 44.2,21.2 41.5,19.2 44.8,19.2"
        fill="#ffffff"
      />
    </>
  ),
  // Saudi Arabia — green field with white shahada band (simplified) and sword.
  SA: (
    <>
      <rect width="60" height="40" fill="#006c35" />
      <rect x="8" y="14" width="44" height="3" fill="#ffffff" />
      <rect x="8" y="20" width="44" height="3" fill="#ffffff" />
      <rect x="10" y="27" width="40" height="2" fill="#ffffff" rx="1" />
      <path d="M12 28 L48 28" stroke="#ffffff" strokeWidth="1.4" />
    </>
  ),
  // Canada — red bars flanking a white square with a red maple leaf (stylised).
  CA: (
    <>
      <rect width="60" height="40" fill="#ffffff" />
      <rect x="0" y="0" width="15" height="40" fill="#d52b1e" />
      <rect x="45" y="0" width="15" height="40" fill="#d52b1e" />
      <path
        d="M30 10 L32 16 L36 15 L34 20 L38 22 L34 24 L36 26 L30 25 L30 30 L30 25 L24 26 L26 24 L22 22 L26 20 L24 15 L28 16 Z"
        fill="#d52b1e"
      />
    </>
  ),
  // United States — 13 stripes with a blue canton and a compact star field.
  US: (
    <>
      <rect width="60" height="40" fill="#ffffff" />
      {[0, 1, 2, 3, 4, 5, 6].map((i) => (
        <rect
          key={i}
          x="0"
          y={i * (40 / 13) * 2}
          width="60"
          height={40 / 13}
          fill="#b22234"
        />
      ))}
      <rect x="0" y="0" width="24" height={40 * (7 / 13)} fill="#3c3b6e" />
      {Array.from({ length: 5 }).flatMap((_, row) =>
        Array.from({ length: 5 }).map((__, col) => (
          <circle
            key={`${row}-${col}`}
            cx={2 + col * 5}
            cy={2 + row * 4}
            r={0.7}
            fill="#ffffff"
          />
        )),
      )}
    </>
  ),
  // Other — neutral globe glyph on chrome-panel background.
  Other: (
    <>
      <rect width="60" height="40" fill="#8a93ab" />
      <circle cx="30" cy="20" r="12" fill="none" stroke="#ffffff" strokeWidth="1.4" />
      <path
        d="M18 20 H42 M30 8 C24 14 24 26 30 32 C36 26 36 14 30 8"
        fill="none"
        stroke="#ffffff"
        strokeWidth="1.2"
      />
    </>
  ),
};

export const Flag = React.forwardRef<SVGSVGElement, FlagProps>(function Flag(
  { region, size = 24, className, title, ...rest },
  ref,
) {
  const label = title ?? `${region} flag`;
  const decorative = rest["aria-hidden"] === true || rest["aria-hidden"] === "true";
  const height = (size * 2) / 3;
  return (
    <svg
      ref={ref}
      role={decorative ? undefined : "img"}
      aria-label={decorative ? undefined : label}
      viewBox="0 0 60 40"
      width={size}
      height={height}
      className={cn("inline-block shrink-0 rounded-chip overflow-hidden", className)}
      {...rest}
    >
      {!decorative && <title>{label}</title>}
      {bodies[region]}
    </svg>
  );
});

Flag.displayName = "Flag";
